import * as React from "react"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"

/**
 * Input — DESIGN.md `text-input`:
 * - White bg, 1px gray-300 border, 6px radius, 32px (h-8) height
 * - 12px h-padding, 12px gray-700 text
 * - Focus: 1px primary border, no ring per DESIGN.md
 */
const inputClassName = (opts: { iconStart?: boolean; iconEnd?: boolean; className?: string }) =>
  cn(
    "h-8 w-full min-w-0 rounded-sm border border-input-border bg-input-background py-1 text-xs text-foreground transition-colors outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-xs file:font-normal file:text-foreground placeholder:text-muted-foreground/80 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40",
    "focus-visible:border-input-focus-border",
    "aria-invalid:border-error aria-invalid:ring-[1px] aria-invalid:ring-error/20",
    opts.iconStart ? "pl-9" : "pl-3",
    opts.iconEnd ? "pr-9" : "pr-3",
    opts.className,
  )

function Input({
  className,
  type,
  iconStart,
  iconEnd,
  ...props
}: React.ComponentProps<"input"> & {
  iconStart?: IconName
  iconEnd?: IconName
}) {
  if (!iconStart && !iconEnd) {
    return (
      <input
        type={type}
        data-slot="input"
        className={inputClassName({ className })}
        {...props}
      />
    )
  }

  return (
    <div data-slot="input-wrapper" className="relative w-full">
      {iconStart ? (
        <span className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2">
          <Icon name={iconStart} size="sm" color="muted" />
        </span>
      ) : null}
      <input
        type={type}
        data-slot="input"
        className={inputClassName({ iconStart: Boolean(iconStart), iconEnd: Boolean(iconEnd), className })}
        {...props}
      />
      {iconEnd ? (
        <span className="pointer-events-none absolute top-1/2 right-3 z-10 -translate-y-1/2">
          <Icon name={iconEnd} size="sm" color="muted" />
        </span>
      ) : null}
    </div>
  )
}

export { Input }
